import React, { useEffect, useState } from 'react'
import Input from '../../components/Input'
import { useLocation, useNavigate } from "react-router-dom";
import style from "../forgotPassword/forgotPassword.module.css";
import icon from "../../assets/images/brand/logo_2_107x70.png";
import { ErrorToast, SuccessToast } from '../../components/toastMessage';
import axios from 'axios';
import { BASE_URL } from '../../constants/Constants';
import CryptoJS from 'crypto-js';
import { _axios } from '../../utils/axios';
 
function ResetPassword() {
    const [email, setEmail] = useState<string | null>(null);
    /** Set instead of email when the reset link is for an account registered without one. */
    const [mobile, setMobile] = useState<string | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState('');
    const [confirmPasswordStrength, setConfirmPasswordStrength] = useState('');
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const location = useLocation()
    const navigate = useNavigate();
 
    useEffect(() => {
        // Parse the query string from the URL
        const parseQueryString = (queryString: string): Record<string, string | null> => {
            const params = new URLSearchParams(queryString);
            return {
                email: params.get('email'),
                mobile: params.get('mobile'),
                role: params.get('role'),
                token: params.get('token'),
            };
        };

        const queryParams = parseQueryString(location.search);

        // Check if the parameters exist before proceeding
        const { email, mobile, role, token } = queryParams;

        const fetchData = async () => {
            try {
                /**
                 * BEFORE — if (!email || !role || !token) { ... return; }
                 * A link for an account without an email carries `mobile`
                 * instead of `email`; either one is enough.
                 */
                if (!(email || mobile) || !role || !token) {
                    console.error('Missing necessary query parameters.');
                    return;
                }

                // Try to decode and decrypt the data
                const encryptedData = decodeURIComponent(token); // Assuming the 'token' contains the encrypted data

                setEmail(email);
                setMobile(mobile);
                setRole(role);
                setToken(token);
 
                // if (encryptedData) {
                //     // Decrypt the data using AES decryption
                //     // const bytes = CryptoJS.AES.decrypt(encryptedData, process.env.REACT_APP_ENCRYPTION_KEY!);

                //     const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
                //     const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
 
                //     // Update the state with the decrypted values
                //     setEmail(decryptedData.email);
                //     setRole(decryptedData.role);
                //     setToken(decryptedData.token);
                // } else {
                //     console.error('No encrypted data found in URL');
                //     // Handle error, perhaps redirect to an error page or show an alert
                // }
            } catch (error) {
                console.error('Error during decryption or fetching data:', error);
            }
        };
 
        fetchData();
    }, [location.search]);
 
 
    const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };
 
    const checkPasswordStrength = (password: string, email: string | null, role: string | null) => {
        // Check for minimum length
        if (password.length < 8) {
            return 'weak';
        }
 
        // Check for at least one uppercase, one lowercase, and one number
        const hasUppercase = /[A-Z]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasNumber = /\d/.test(password);
 
        // Check for strong password
        if (hasUppercase && hasLowercase && hasNumber) {
            // Check for no sequential patterns or common sequences
            const emailPattern = email ? escapeRegExp(email) : '';
            const rolePattern = role ? escapeRegExp(role) : '';
            const sequentialPattern = /(?=.*123|234|345|456|567|678|789|987|876|765|654|543|432|321)(?=.*abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/;
            if (sequentialPattern.test(password) || new RegExp(`(${emailPattern})|(${rolePattern})`).test(password)) {
                return 'strong';
            }
            return 'strong';
        }
 
        // If uppercase, lowercase, and number checks fail, return 'medium'
        return 'medium';
    };
 
    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPassword(value);
        setPasswordStrength(checkPasswordStrength(value, email, role));
        setPasswordsMatch(value === confirmPassword);
    };
 
    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setConfirmPassword(value);
        setConfirmPasswordStrength(checkPasswordStrength(value, email, role));
        setPasswordsMatch(value === password);
    };
 
    const getPasswordStrengthColor = (strength: string) => {
        switch (strength) {
            case 'strong':
                return 'text-success';
            case 'medium':
                return 'text-warning';
            case 'weak':
                return 'text-danger';
            default:
                return 'text-secondary';
        }
    };
 
    const handleNext = () => {

        /** BEFORE — if (!email || !role || !token); `mobile` now stands in for a missing email. */
        if (!(email || mobile) || !role || !token) {
            ErrorToast('Missing required data. Please try again.');
            return;
        }
 
        if (passwordStrength === 'weak' || passwordStrength === 'medium') {
            ErrorToast("Please enter a password with a minimum of 8 characters and ensure it is not sequential (e.g. 123/abc).");
            return;
        }
        if (password !== confirmPassword) {
            ErrorToast('Please enter the same password in both inputs');
            return;
        }
        /** BEFORE — body was { email, role, password, token }; mobile added for accounts without an email. */
        _axios.post(`${BASE_URL}/api/forgot-password/verify`, { email, mobile, role, password, token })
            .then((details) => {
                console.log('response', details);
                navigate('/onboarding/login');
                SuccessToast('Password changed successfully');
            }).catch((error) => {
                console.log(error);
                ErrorToast("Your link has expired or is invalid.");
            });
    };
 
 
    return (
        <div>
            <div className={style.loginContainer}>
                <div className={style.loginForm}>
                    <div className={style.logo}>
                        <img src={icon} alt="Logo" />
                    </div>
                    <div>
                        <>
                            <div className={style.loginText}>Change Password</div>
 
                            <br />
 
                            <Input
                                title={"New Password"}
                                placeholder={"Password"}
                                isIcon={true}
                                icon={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                                type={showPassword ? "text" : "password"}
                                onClick={() => setShowPassword(!showPassword)}
                                value={password}
                                onChange={handlePasswordChange}
                            />
 
                            <div className={style.passwordStrength}>
                                <span>Password strength: </span>
                                <span className={`${style.strengthIndicator} ${getPasswordStrengthColor(passwordStrength)}`}>
                                    {passwordStrength || '--'}
                                </span>
                            </div>
 
                            <Input
                                title={"Confirm Password"}
                                placeholder={"Confirm Password"}
                                isIcon={true}
                                type={showConfirmPassword ? "text" : "password"}
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                value={confirmPassword}
                                icon={`fa ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}
                                onChange={handleConfirmPasswordChange}
                            />
 
                            <div className={style.passwordStrength}>
                                <span>Confirm password strength: </span>
                                <span className={`${style.strengthIndicator} ${getPasswordStrengthColor(confirmPasswordStrength)}`}>
                                    {confirmPasswordStrength || '--'}
                                </span>
                            </div>
 
                            <button
                                onClick={handleNext}
                                className={style.sendButton}
                            // disabled={passwordStrength !== 'strong' || !passwordsMatch}
                            >
                                Update
                            </button>
                            <div className={style.passwordHint}>
                                Password must be strong: Minimum 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
                                has context menu</div>
                        </>
                    </div>
                </div>
            </div>
        </div>
    )
}
 
export default ResetPassword
